/**
 * learn.4klabs.ai — one lesson, in a glass proscenium.
 *
 * The page's light is this lesson's SINGLE subject, so a Science lesson glows
 * pure teal. The production embed player (/embed/$shareToken) sits inside a
 * glass frame on that wash; the side rail is glass cards; and "Up next" is a
 * glass sliver that peeks in from the right gutter — a gutter the grid reserves
 * for it, so it never lands on the check card.
 */

import { createFileRoute, notFound, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { MessageSquare, ArrowLeft, Zap, Loader2, CheckCircle2, Play } from 'lucide-react';
import { cn } from '../lib/utils';
import { fetchCatalogItem, fetchCourse, type CourseDetail } from '../services/learnAPI';
import { fetchLaunchTicket } from '../services/learnerAPI';
import { useLearnerProgress, formatWatchTime } from '../components/learn/useLearnerProgress';
import {
  useLearnTheme,
  LearnRoot,
  LearnTopBar,
  subjectMeta,
  glassHero,
  glassPanel,
  glassChip,
  inkMuted,
} from '../components/learn/learnShared';
import { ExperienceFeedbackWidget } from '../components/learn/ExperienceFeedbackWidget';
import { LessonSurveyPanel } from '../components/learn/LessonSurveyPanel';
import { AssessmentLauncher } from '../components/learn/AssessmentLauncher';
import { LearnAccountMenu } from '../components/learn/LearnAccountMenu';

export const Route = createFileRoute('/learn/$shareToken')({
  component: LearnPlayer,
  loader: async ({ params }) => {
    const item = await fetchCatalogItem(params.shareToken);
    if (!item) throw notFound();
    const course = item.courseId ? await fetchCourse(item.courseId).catch(() => null) : null;
    return { item, course };
  },
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center bg-[#FBFAF7] text-center text-[#191831] dark:bg-[#0D0E16] dark:text-[#EFEEF8]">
      <div className="space-y-3">
        <div className="text-5xl">🔒</div>
        <h1 className="font-serif text-3xl">Experience not found</h1>
        <p className="max-w-sm text-[#575277] dark:text-[#A8A5C4]">
          This experience may not be published, or the link is invalid.
        </p>
        <Link to="/learn" className="inline-block font-semibold text-[#5B2EE5] underline dark:text-[#B69BF5]">
          Back to catalog
        </Link>
      </div>
    </div>
  ),
});

const eyebrow = 'text-[10.5px] font-bold uppercase tracking-[0.19em]';

/**
 * Curriculum standard codes (L.11.3, 8.G.A.3, …) never reach a learner — the
 * grade band is the only alignment we show. The curated `unit` field is free
 * text, so anything code-shaped is dropped rather than rendered.
 */
const STANDARD_CODE = /^[A-Za-z0-9]+(\.[A-Za-z0-9]+){2,}$/;

function LearnPlayer() {
  const { item, course } = Route.useLoaderData();
  // The lesson after this one in its course — what "Up next" points at.
  const flat = course?.chapters.flatMap((c) => c.lessons) ?? [];
  const nextLesson = (() => {
    const i = flat.findIndex((l) => l.shareToken === item.shareToken);
    return i >= 0 && i + 1 < flat.length ? flat[i + 1] : null;
  })();
  const { theme, toggle } = useLearnTheme();
  const subject = item.chapter ?? item.subject;
  const { color } = subjectMeta(subject);
  const unitLabel = item.unit && !STANDARD_CODE.test(item.unit.trim()) ? item.unit : null;

  // The player URL is minted server-side per view: it carries this learner's
  // actor id AND a signature proving we issued it for this lesson. Rendering
  // the iframe before it arrives would start an ANONYMOUS view that no later
  // URL change could re-attribute, so hold the frame until the ticket lands.
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLaunchUrl(null);
    setLaunchError(false);
    fetchLaunchTicket(item.shareToken)
      .then((ticket) => {
        if (!cancelled) setLaunchUrl(ticket.playUrl);
      })
      .catch(() => {
        if (!cancelled) setLaunchError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [item.shareToken]);

  return (
    // A lesson page glows its ONE subject — the wash is the curriculum.
    <LearnRoot theme={theme} subjects={subject ? [subject] : []}>
      <LearnTopBar
        theme={theme}
        onToggle={toggle}
        right={
          <>
            <Link
              to="/learn"
              className={cn(
                'mr-1 hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors sm:inline-flex',
                'hover:text-[#191831] dark:hover:text-white',
                inkMuted,
              )}
            >
              <ArrowLeft className="h-4 w-4" /> Catalog
            </Link>
            <LearnAccountMenu />
          </>
        }
      />

      <main className="learn-shell mx-auto w-full px-4 pb-20 pt-6 sm:px-6">
        {/* The right gutter is the sliver's home: it peeks into empty page, never onto the rail. */}
        <div className="relative overflow-hidden lg:pr-[104px]">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_336px]">
            <div className="min-w-0">
              {/* The proscenium — glass around the production embed player. */}
              <div className={cn(glassHero, 'p-2.5 sm:p-3.5')}>
                <div className="relative aspect-video overflow-hidden rounded-[18px] bg-[#0C0B18]">
                  {launchUrl ? (
                    <iframe
                      // The signed launch URL from the server: baked CDN bundle when
                      // available, /embed as the pre-bake fallback, either way carrying
                      // ?actor=&sig= so this view is attributed to the signed-in learner.
                      src={launchUrl}
                      title={item.title ?? 'Interactive experience'}
                      className="absolute inset-0 h-full w-full border-0"
                      // Grant fullscreen/autoplay to the player origin too: the /embed fallback
                      // redirects cross-origin to play.4klabs.ai, and a bare allowlist defaults
                      // to 'src' (app.4klabs.ai), which would drop the permission post-redirect.
                      allow="autoplay 'src' https://play.4klabs.ai; fullscreen 'src' https://play.4klabs.ai"
                      allowFullScreen
                    />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center text-white/70">
                      {launchError ? (
                        <p className="px-6 text-center text-sm">
                          We could not start this lesson. Reload the page to try again.
                        </p>
                      ) : (
                        <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* This block reads straight off the wash, so it carries its own glass. */}
              <div className={cn(glassPanel, 'mt-5 px-5 py-5 sm:px-6')}>
                {course ? (
                  <Link
                    to="/learn/course/$courseId"
                    params={{ courseId: course.course.id }}
                    className={cn(
                      eyebrow,
                      'inline-flex flex-wrap items-center gap-2 tracking-[0.14em] transition-colors',
                      'hover:text-[#191831] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:hover:text-white',
                      inkMuted,
                    )}
                  >
                    {subject && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />}
                    <span>{course.course.title}</span>
                    {subject && (
                      <>
                        <span aria-hidden="true" className="opacity-50">
                          /
                        </span>
                        <span style={{ color }} className="dark:brightness-150">
                          {subject}
                        </span>
                      </>
                    )}
                  </Link>
                ) : (
                  subject && (
                    <p className={cn(eyebrow, 'inline-flex items-center gap-2 tracking-[0.14em]')} style={{ color }}>
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />
                      <span className="dark:brightness-150">{subject}</span>
                    </p>
                  )
                )}

                <h1 className="mt-2 break-words font-serif text-[28px] leading-[1.16] tracking-[-0.015em] sm:text-[35px]">
                  {item.title ?? 'Interactive experience'}
                </h1>

                <div className="mt-3.5 flex flex-wrap items-center gap-2 text-[11.5px] font-semibold">
                  {item.grade && (
                    <span className={cn(glassChip, 'px-3 py-1 text-[#5B2EE5] dark:text-[#B69BF5]')}>{item.grade}</span>
                  )}
                  <span className={cn(glassChip, 'inline-flex items-center gap-1.5 px-3 py-1')} style={{ color }}>
                    <Zap className="h-3 w-3" />
                    <span className="dark:brightness-150">interactive</span>
                  </span>
                  {unitLabel && (
                    <span className={cn(glassChip, 'px-3 py-1 font-medium', inkMuted)}>{unitLabel}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Side rail — glass cards */}
            <aside className="flex min-w-0 flex-col gap-5">
              <LessonProgressCard shareToken={item.shareToken} accent={color} />

              <AssessmentLauncher shareToken={item.shareToken} />

              {/* The considered survey. The chips widget below it is the one-tap
                  version — different instruments, kept apart deliberately. */}
              <LessonSurveyPanel
                key={item.shareToken}
                shareToken={item.shareToken}
                courseId={item.courseId ?? null}
                chapter={item.chapter ?? null}
              />

              {item.allowFeedback && (
                <ExperienceFeedbackWidget
                  key={item.shareToken}
                  shareToken={item.shareToken}
                  averageRating={item.averageRating}
                  ratingCount={item.ratingCount}
                />
              )}

              {item.allowReviews && (
                <div className={cn(glassPanel, 'p-5')}>
                  <h2 className="font-serif text-[22px] leading-[1.28]">Give precise feedback</h2>
                  <p className={cn('mt-2 text-[13px] leading-relaxed', inkMuted)}>
                    Pause at any moment and pin a comment to that exact second of the experience.
                  </p>
                  <a
                    href={`/video/${item.shareToken}?review=true`}
                    target="_blank"
                    rel="noreferrer"
                    className={cn(
                      'mt-3.5 inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold',
                      'border border-[#5B2EE5]/35 bg-[#5B2EE5]/10 text-[#5B2EE5] transition-colors hover:bg-[#5B2EE5]/[0.18]',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5]',
                      'dark:border-[#B69BF5]/40 dark:bg-[#B69BF5]/[0.12] dark:text-[#CDB7FA]',
                    )}
                  >
                    <MessageSquare className="h-4 w-4" /> Open review mode
                  </a>
                </div>
              )}
            </aside>
          </div>

          {nextLesson && course && <UpNextSliver lesson={nextLesson} course={course} />}
        </div>
      </main>
    </LearnRoot>
  );
}

/**
 * The next lesson in the course — what turns a catalogue into a sequence.
 *
 * At lg and up it is a sliver parked in the reserved right gutter, showing its
 * left edge and sliding fully into view on hover or keyboard focus. Below lg it
 * is simply the last card in the stack: one component, no mobile-only branch.
 */
function UpNextSliver({
  lesson,
  course,
}: {
  lesson: CourseDetail['chapters'][number]['lessons'][number];
  course: CourseDetail;
}) {
  return (
    <Link
      to="/learn/$shareToken"
      params={{ shareToken: lesson.shareToken }}
      className={cn(
        glassHero,
        'group mt-6 block w-full p-3.5',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5]',
        // The sliver: 264px wide, parked so ~96px shows inside the 104px gutter.
        'lg:absolute lg:right-0 lg:top-[176px] lg:z-10 lg:mt-0 lg:w-[264px] lg:rounded-r-none',
        'lg:translate-x-[168px] lg:transition-transform lg:duration-300 lg:ease-out',
        'lg:hover:translate-x-0 lg:focus-visible:translate-x-0',
        'motion-reduce:lg:transition-none',
      )}
    >
      <p className={cn(eyebrow, inkMuted)}>Up next</p>
      <div className="relative mt-2.5 aspect-video overflow-hidden rounded-[14px] bg-[#151427]">
        {lesson.thumbnailUrl && (
          <img
            src={lesson.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        )}
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-white/92 shadow-lg">
            <Play className="h-4 w-4 fill-[#191831] text-[#191831]" />
          </span>
        </span>
      </div>
      <h2 className="mt-2.5 line-clamp-2 break-words font-serif text-[17px] leading-[1.2]">
        {lesson.title ?? 'Next lesson'}
      </h2>
      <p className={cn('mt-1.5 truncate text-[12px]', inkMuted)}>{course.course.title}</p>
    </Link>
  );
}

/**
 * This learner's standing on this lesson. Rendered only once they have started
 * it — an untouched lesson has nothing to say, and a "0%" badge on first open
 * reads as failure rather than a fresh start.
 */
function LessonProgressCard({ shareToken, accent }: { shareToken: string; accent: string }) {
  const { byToken, loading } = useLearnerProgress();
  const progress = byToken.get(shareToken);

  if (loading || !progress || progress.status === 'not_started') return null;

  const done = progress.status === 'completed';

  return (
    <div className={cn(glassPanel, 'p-5')}>
      <div className="flex items-center justify-between gap-3">
        <p className={cn(eyebrow, inkMuted)}>Your progress</p>
        {done ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#0B5B52]/[0.14] px-2.5 py-1 text-[12px] font-semibold text-[#0B5B52] dark:bg-[#2DD4BF]/[0.16] dark:text-[#7FE7DA]">
            <CheckCircle2 className="h-3.5 w-3.5" /> Completed
          </span>
        ) : (
          <span className="font-serif text-[26px] leading-none">{progress.maxPercent}%</span>
        )}
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#191831]/[0.13] dark:bg-white/20">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(4, progress.maxPercent)}%`,
            background: done ? '#0B5B52' : accent,
          }}
        />
      </div>

      <p className={cn('mt-3 text-[13px] leading-relaxed', inkMuted)}>
        {formatWatchTime(progress.watchTimeMs)} watched
        {progress.views > 1 && ` across ${progress.views} visits`}
        {progress.linkedTest?.taken &&
          progress.linkedTest.bestScorePercent != null &&
          ` · check score ${progress.linkedTest.bestScorePercent}%`}
      </p>
    </div>
  );
}
